import React, { useState, useImperativeHandle, forwardRef, useEffect, useRef, useCallback } from 'react'
import { APIProvider, Map, AdvancedMarker, InfoWindow, useMap, useMapsLibrary } from '@vis.gl/react-google-maps'
import { trans } from '../../../../../_providers/Translation'

// images
import popupDefaultHeadImage from "@/assets/img/modals/icons/head.svg"
import markerImg from '../../../../../assets/img/leeflet/marker-icon.png'

import '../../../../../assets/css/modals/employees.css'
import { Maps } from '../../../../../_config/maps'
import loader from '../../../../../assets/img/loader.svg'

let timeout

const CENTER = { lat: 42.70060440808085, lng: 23.318481445312504 }

// ─── Outer wrapper: provides the Google Maps API context ─────────────────────
function Address(props, ref) {
    return (
        <APIProvider apiKey={Maps.google}>
            <AddressContent {...props} forwardedRef={ref} />
        </APIProvider>
    )
}

// ─── Main content component ───────────────────────────────────────────────────
function AddressContent({ forwardedRef, ...props }) {
    const inputRef = useRef(null)

    const placesLib = useMapsLibrary('places')

    const [state, setState] = useState({
        overlay: false,
        modal: false,
        loading: false,
        selected: null,
        lat: null,
        lon: null,
        place_id: null,
        data: [],
        dataLoaded: false,
        onSave: null,
    })

    const [services, setServices] = useState({
        autocomplete: null,
        geocoder: null,
    })

    useImperativeHandle(forwardedRef, () => ({
        open: () => handleOpen(),
        close: () => handleClose(),
        onSave: fn => setState(prev => ({ ...prev, onSave: fn })),
    }))

    // Initialize Google Places / Geocoder services once the library is ready
    useEffect(() => {
        if (placesLib && window.google) {
            setServices({
                autocomplete: new placesLib.AutocompleteService(),
                geocoder: new window.google.maps.Geocoder(),
            })
        }
    }, [placesLib])

    // Pre-populate from props (selected address + coordinates)
    useEffect(() => {
        handleSelect(props.selected, props.params.lat, props.params.lon)
    }, [props.selected, props.params.lat, props.params.lon])

    // ── Handlers ──────────────────────────────────────────────────────────────
    const handleOpen = () => {
        setState(prev => ({ ...prev, overlay: true }))
        setTimeout(() => setState(prev => ({ ...prev, modal: true })), 50)
    }

    const handleClose = e => {
        if (e) e.preventDefault()
        setState(prev => ({ ...prev, modal: false }))
        setTimeout(() => setState(prev => ({ ...prev, overlay: false })), 50)
    }

    const handleSelect = (name, lat, lon) => {
        setState(prev => ({ ...prev, selected: name, lat, lon }))
    }

    const handleSelectLatLon = useCallback((lat, lon, place_id = null) => {
        setState(prev => ({ ...prev, lat, lon, place_id }))
    }, [])

    const handleSave = e => {
        e.preventDefault()
        saveSelected(state.selected, state.lat, state.lon)
        handleClose()
    }

    const saveSelected = (selected, lat, lon) => {
        if (typeof props.onSave === 'function') {
            props.onSave(selected, lat, lon)
        } else if (typeof state.onSave === 'function') {
            state.onSave(selected, lat, lon)
        }
    }

    const handleSearch = e => {
        const name = e.target.value

        setState(prev => ({ ...prev, selected: name, loading: true }))

        clearTimeout(timeout)
        timeout = setTimeout(() => findInGoogle(name), 900)
    }

    const findInGoogle = name => {
        if (!services.autocomplete) return

        services.autocomplete.getQueryPredictions({ input: name }, (predictions, status) => {
            if (status !== window.google.maps.places.PlacesServiceStatus.OK || !predictions) {
                setState(prev => ({ ...prev, data: [], loading: false }))
                return
            }

            const mapped = predictions.map(p => ({ ...p, display_name: p.description }))
            setState(prev => ({ ...prev, data: mapped, loading: false }))
        })
    }

    const handleSelectGooglePlace = place => {
        if (!services.geocoder) return

        setState(prev => ({ ...prev, selected: place.display_name, data: [] }))

        services.geocoder.geocode({ placeId: place.place_id }).then(({ results }) => {
            if (results && results[0]) {
                const lat = results[0].geometry.location.lat()
                const lon = results[0].geometry.location.lng()
                handleSelectLatLon(lat, lon, place.place_id)
            }
        })
    }

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className={`${state.overlay ? 'visible' : ''} overlay`}>
            <div className={`${state.modal ? 'show' : ''} popup-primary`}>

                <div className='head-new'>
                    <img src={popupDefaultHeadImage} alt="icon" />
                    <div className='texts'>
                        <h2>{trans('labels.address')}</h2>
                        <p>{trans('subheadings.address')}</p>
                    </div>
                    <span className='close' onClick={handleClose}></span>
                </div>

                <div id="map-wrapper">
                    <div className="left">
                        <input
                            ref={inputRef}
                            type="search"
                            value={state.selected || ''}
                            onChange={handleSearch}
                            style={{ width: '100%' }}
                        />
                        {state.loading
                            ? <img className="loader" src={loader} />
                            : <ul>
                                {state.data.map(e =>
                                    <li
                                        key={'e-' + e.place_id}
                                        onClick={() => handleSelectGooglePlace(e)}
                                        className={e.place_id === state.place_id ? 'active' : ''}
                                    >
                                        <span>{e.display_name}</span>
                                    </li>
                                )}
                            </ul>
                        }
                    </div>

                    <div className="right">
                        <div className="map">
                            <Map
                                defaultCenter={CENTER}
                                defaultZoom={5}
                                mapId="DEMO_MAP_ID"
                                style={{ width: '100%', height: '100%' }}
                                disableDefaultUI={true}
                                keyboardShortcuts={false}
                            >
                                <MapOverlays
                                    state={state}
                                    handleSelectLatLon={handleSelectLatLon}
                                    inputRef={inputRef}
                                />
                            </Map>
                        </div>
                    </div>
                </div>

                <div className="footer align-right">
                    <button onClick={handleClose}>
                        {trans('buttons.cancel')}
                    </button>
                    <button className="button-primary" onClick={handleSave}>
                        {trans('buttons.save')}
                    </button>
                </div>

            </div>
        </div>
    )
}

// ─── Map overlays (must live inside <Map> context) ────────────────────────────
function MapOverlays({ state, handleSelectLatLon, inputRef }) {
    const map = useMap()
    const [infoWindowOpen, setInfoWindowOpen] = useState(false)

    // Map click → place marker
    useEffect(() => {
        if (!map) return
        const listener = map.addListener('click', e => {
            handleSelectLatLon(e.latLng.lat(), e.latLng.lng())
        })
        return () => window.google.maps.event.removeListener(listener)
    }, [map, handleSelectLatLon])

    // When a location is set, zoom into it and auto-open the InfoWindow
    useEffect(() => {
        if (!map || !state.lat || !state.lon) return
        const lat = parseFloat(state.lat)
        const lng = parseFloat(state.lon)
        if (!isFinite(lat) || !isFinite(lng)) return
        map.setCenter({ lat, lng })
        map.setZoom(20)
    }, [map, state.lat, state.lon])

    // On modal open: trigger resize and focus the search input
    useEffect(() => {
        if (!map || !state.overlay || !state.modal) return
        window.google.maps.event.trigger(map, 'resize')
        if (inputRef?.current) inputRef.current.focus()
    }, [map, state.overlay, state.modal])

    if (!state.lat || !state.lon) return null

    const lat = parseFloat(state.lat)
    const lng = parseFloat(state.lon)
    if (!isFinite(lat) || !isFinite(lng)) return null

    const pos = { lat, lng }

    return (
        <>
            <AdvancedMarker position={pos}>
                <img src={markerImg} width={25} height={41} alt="location" />
            </AdvancedMarker>
        </>
    )
}

export default React.memo(forwardRef(Address), (prev, next) => {
    return JSON.stringify(prev) === JSON.stringify(next)
})
